import * as React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@ui/lib/utils';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { OptionsType } from '@app/utils/types';
import { isValidEmail } from '@data/utils/validation';

export type FormOptionType = Required<OptionsType> & { isFixed: boolean };

function getEmailsErrorMessage(error: unknown): string | undefined {
  if (error == null) return undefined;
  if (Array.isArray(error)) {
    const nested = error.find((e: { value?: { message?: string } }) => e?.value?.message);
    if (nested?.value?.message) return nested.value.message;
    const first = error[0] as { message?: string } | undefined;
    return first?.message;
  }
  if (typeof error === 'object' && 'message' in (error as object)) {
    return String((error as { message?: string }).message ?? '');
  }
  return undefined;
}

export type ApprovalRecipientsFieldHandle = {
  /** Flush a valid email still typed in the box (e.g. on submit) and return the resulting list, or
   * `null` if the text is present but not a valid email so the caller can block submission. */
  commitDraft: () => FormOptionType[] | null;
};

export type ApprovalRecipientsFieldProps = {
  /** A node, not just a string, so a caller that already names the row can hide the label from sight. */
  label: React.ReactNode;
  /** For a caller that draws its own box: border, shadow and padding come off, so a To line is one row. */
  bare?: boolean;
  placeholder?: string;
  /** Shown when the user commits a draft that is not a valid email (blur / Enter). */
  invalidEmailMessage?: string;
  /** Why a fixed recipient cannot be taken off — on the chip's tooltip and read out beside it. */
  fixedHint?: string;
  value: FormOptionType[];
  onChange: (next: FormOptionType[]) => void;
  onBlur: () => void;
  disabled?: boolean;
  error?: unknown;
  onDraftInvalid?: () => void;
};

// An email "To" box: recipients show as removable chips, and typing an address (Enter or blur) adds
// another. Colleagues ticked in the list next to it are pushed in here as chips too.
export const ApprovalRecipientsField = React.forwardRef<ApprovalRecipientsFieldHandle, ApprovalRecipientsFieldProps>(
  (
    {
      label,
      bare = false,
      placeholder,
      invalidEmailMessage = 'Must be a valid email',
      fixedHint,
      value,
      onChange,
      onBlur,
      disabled,
      error,
      onDraftInvalid,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const baseId = React.useId();
    const [draft, setDraft] = React.useState('');
    const [draftError, setDraftError] = React.useState<string | null>(null);
    const errorMessage = getEmailsErrorMessage(error);
    const showFieldErrorOutline = !!(errorMessage || draftError);

    const valueRef = React.useRef(value);
    React.useEffect(() => {
      valueRef.current = value;
    }, [value]);

    const commitDraft = (): FormOptionType[] | null => {
      const trimmed = draft.trim();
      const { current } = valueRef;
      if (!trimmed) return current;
      if (current.some(x => x.value.toLowerCase() === trimmed.toLowerCase())) {
        setDraft('');
        setDraftError(null);
        return current;
      }
      if (!isValidEmail(trimmed)) {
        setDraftError(invalidEmailMessage);
        onDraftInvalid?.();
        return null;
      }
      const next = [...current, { value: trimmed, label: trimmed, isFixed: false }];
      valueRef.current = next;
      onChange(next);
      setDraft('');
      setDraftError(null);
      return next;
    };

    const remove = (email: string) => {
      const next = valueRef.current.filter(x => x.value !== email);
      valueRef.current = next;
      onChange(next);
    };

    React.useImperativeHandle(ref, () => ({ commitDraft }));

    return (
      <div className={cn('flex flex-col', !bare && 'gap-1.5')}>
        <Label htmlFor={`${baseId}-input`}>{label}</Label>
        <div
          className={cn(
            // One spacing all round — 6px of padding, and 6px between chips — so a chip sits the same
            // distance from the edge whichever edge it is near. Grows only as chips wrap onto another
            // line: the inner input is kept to a chip's height so an empty box is not three lines tall.
            'flex min-h-8 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-transparent p-1.5 shadow-xs transition-colors',
            // Rings for the address being typed, not for the button that removes one. `focus-within`
            // caught the remove buttons too, so pressing a cross lit the whole box up in the brand
            // colour for as long as it took the chip to go.
            'has-[input:focus-visible]:border-ring has-[input:focus-visible]:ring-3 has-[input:focus-visible]:ring-ring/50',
            showFieldErrorOutline &&
              'border-destructive has-[input:focus-visible]:border-destructive has-[input:focus-visible]:ring-destructive/20',
            bare &&
              'min-h-7 rounded-none border-0 p-0 shadow-none has-[input:focus-visible]:border-transparent has-[input:focus-visible]:ring-0',
          )}
        >
          {value.map(opt => (
            <span
              key={opt.value}
              // A fixed recipient is drawn as an outline where a removable one is filled, so the two read
              // as different kinds of thing before anyone looks for a cross to press. No badge or icon on
              // it: the rule belongs in the sentence above the field, not in a symbol to decode.
              className={cn(
                'inline-flex max-w-full items-center gap-1 rounded-md px-2.5 py-1 text-sm text-foreground',
                opt.isFixed ? 'border border-border bg-transparent' : 'bg-muted',
              )}
              title={opt.isFixed ? fixedHint : undefined}
            >
              <span className="min-w-0 truncate">{opt.label}</span>
              {/* An outline says nothing to a screen reader, so the reason is read out instead. */}
              {opt.isFixed ? <span className="sr-only">{fixedHint}</span> : null}
              {!opt.isFixed && !disabled ? (
                <button
                  type="button"
                  className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={() => remove(opt.value)}
                  aria-label={`${t('actions.remove')} ${opt.value}`}
                >
                  <X className="size-3.5 shrink-0" />
                </button>
              ) : null}
            </span>
          ))}
          <Input
            id={`${baseId}-input`}
            type="text"
            autoComplete="email"
            inputMode="email"
            disabled={disabled}
            aria-invalid={showFieldErrorOutline}
            value={draft}
            onFocus={() => setDraftError(null)}
            onChange={e => {
              setDraft(e.target.value);
              setDraftError(null);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                commitDraft();
              }
            }}
            onBlur={() => {
              if (commitDraft()) {
                onBlur();
              }
            }}
            placeholder={value.length === 0 ? placeholder : undefined}
            className="h-6 min-w-[10rem] flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
        </div>
        {(draftError || errorMessage) && <p className="text-sm text-destructive">{draftError || errorMessage}</p>}
      </div>
    );
  },
);
ApprovalRecipientsField.displayName = 'ApprovalRecipientsField';
